import React from 'react';
import { Box, Container, Grid, Typography, Card, CardContent, useTheme, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SectionHeader from '../SectionHeader';
import PersonIcon from '@mui/icons-material/Person';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import StoreButtons from '../StoreButtons';

const FeatureCard = ({ title, description, icon, delay }) => {
  const theme = useTheme();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay }}
      style={{ height: '100%' }}
    >
      <Card
        sx={{
          height: '100%',
          borderRadius: 2,
          transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-10px)',
            boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'rgba(46, 80, 119, 0.1)',
                borderRadius: '50%',
                width: 80,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
          </Box>
          <Typography variant="h5" component="h3" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1, textAlign: 'center' }}>
            {description}
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const FeaturesSection = () => {
  const theme = useTheme();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      title: 'Для Администраторов',
      description: 'Управление записями пациентов и контроль коммуникации между врачами и пациентами.',
      icon: <AdminPanelSettingsIcon fontSize="large" color="primary" />,
    },
    {
      title: 'Для Врачей',
      description: 'Безопасное общение с пациентами и возможность указать специализацию в клинике.',
      icon: <HealthAndSafetyIcon fontSize="large" color="primary" />,
    },
    {
      title: 'Для Пациентов',
      description: 'Прямая безопасная связь с лечащими врачами и хранение истории общения.',
      icon: <PersonIcon fontSize="large" color="primary" />,
    },
    {
      title: 'Для Родственников',
      description: 'Доступ к чату для коммуникации с врачами от имени пациента.',
      icon: <FamilyRestroomIcon fontSize="large" color="primary" />,
    },
  ];

  const workflowSteps = [
    {
      title: 'Первоначальная настройка',
      description: 'Выбор роли и настройка профиля.',
      number: '01',
    },
    {
      title: 'Регистрация пациентов',
      description: 'Администраторы создают для каждого пациента уникальный код чата.',
      number: '02',
    },
    {
      title: 'Подключение к чату',
      description: 'Пациенты используют код для присоединения к чату с врачом.',
      number: '03',
    },
    {
      title: 'Коммуникация',
      description: 'Обмен сообщениями в защищенной среде с шифрованием.',
      number: '04',
    },
    {
      title: 'Непрерывность лечения',
      description: 'Сохранение истории сообщений для обеспечения непрерывности лечения.',
      number: '05',
    },
  ];

  return (
    <Box
      id="features"
      sx={{
        py: 10,
        backgroundColor: theme.palette.background.default,
      }}
      ref={ref}
    >
      <Container maxWidth="lg">
        <SectionHeader
          title="Ключевые функции"
          subtitle="ДокМедЧат обеспечивает эффективную и безопасную коммуникацию между всеми участниками лечения."
        />

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <FeatureCard
                title={feature.title}
                description={feature.description}
                icon={feature.icon}
                delay={index * 0.1}
              />
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 6, mb: 6, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            <Typography variant="h5" sx={{ mb: 3, color: 'primary.main', fontWeight: 500 }}>
              Скачайте приложение уже сейчас
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              Доступно для iOS и Android устройств
            </Typography>
            <StoreButtons />
          </motion.div>
        </Box>

        <Box sx={{ mt: 10 }}>
          <SectionHeader
            title="Как это работает"
            subtitle="Процесс использования ДокМедЧат интуитивно понятен для пользователей."
          />

          <Box sx={{ mt: 5 }}>
            {workflowSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                transition={{ duration: 0.7, delay: index * 0.15 }}
              >
                <Grid
                  container
                  spacing={3}
                  alignItems="center"
                  sx={{
                    mb: 4,
                    flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
                  }}
                >
                  <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        borderRadius: '50%',
                        width: 80,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                      }}
                    >
                      {step.number}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={10}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {step.description}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </motion.div>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturesSection; 